<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">AI Assistant</h1>
            <p class="text-slate-400">Your AI-powered business intelligence and automation platform</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showCreateAgentModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Create Agent
            </button>
            <router-link to="/company/marketplace" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              AI Marketplace
            </router-link>
          </div>
        </div>
      </div>

      <!-- Hero Stats Section -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Active Agents</p>
            <p class="text-3xl font-bold text-white">{{ aiStats.activeAgents }}</p>
            <p class="text-sm text-emerald-400">deployed</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Tasks Automated</p>
            <p class="text-3xl font-bold text-white">{{ aiStats.tasksAutomated }}</p>
            <p class="text-sm text-cyan-400">this month</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Time Saved</p>
            <p class="text-3xl font-bold text-white">{{ aiStats.timeSaved }}h</p>
            <p class="text-sm text-amber-400">this month</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Success Rate</p>
            <p class="text-3xl font-bold text-white">{{ aiStats.successRate }}%</p>
            <p class="text-sm text-purple-400">automation</p>
          </div>
        </div>
      </div>

      <!-- Training Progress Dashboard -->
      <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-8">
        <h2 class="text-xl font-semibold text-white mb-4">Business Intelligence Training</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="bg-slate-700/30 rounded-xl p-4">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-medium text-white">Crew Management</h3>
              <span class="text-xs text-emerald-400">{{ trainingProgress.crewManagement }}%</span>
            </div>
            <div class="w-full bg-slate-600 rounded-full h-2">
              <div class="bg-emerald-500 h-2 rounded-full transition-all duration-300" :style="{ width: trainingProgress.crewManagement + '%' }"></div>
            </div>
          </div>
          
          <div class="bg-slate-700/30 rounded-xl p-4">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-medium text-white">Financial Analysis</h3>
              <span class="text-xs text-cyan-400">{{ trainingProgress.financialAnalysis }}%</span>
            </div>
            <div class="w-full bg-slate-600 rounded-full h-2">
              <div class="bg-cyan-500 h-2 rounded-full transition-all duration-300" :style="{ width: trainingProgress.financialAnalysis + '%' }"></div>
            </div>
          </div>
          
          <div class="bg-slate-700/30 rounded-xl p-4">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-medium text-white">Compliance Monitoring</h3>
              <span class="text-xs text-amber-400">{{ trainingProgress.complianceMonitoring }}%</span>
            </div>
            <div class="w-full bg-slate-600 rounded-full h-2">
              <div class="bg-amber-500 h-2 rounded-full transition-all duration-300" :style="{ width: trainingProgress.complianceMonitoring + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Chat Interface -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Chat History -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 h-96 overflow-y-auto">
            <h3 class="text-lg font-semibold text-white mb-4">Recent Conversations</h3>
            <div class="space-y-3">
              <div 
                v-for="chat in chatHistory" 
                :key="chat.id"
                @click="selectChat(chat)"
                class="p-3 bg-slate-700/30 rounded-lg cursor-pointer hover:bg-slate-600/50 transition-colors"
                :class="{ 'bg-emerald-500/20 border border-emerald-500/30': selectedChat?.id === chat.id }"
              >
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <span class="text-xs font-bold text-white">{{ chat.agent.charAt(0) }}</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-white truncate">{{ chat.agent }}</p>
                    <p class="text-xs text-slate-400 truncate">{{ chat.lastMessage }}</p>
                  </div>
                  <span class="text-xs text-slate-500">{{ formatTime(chat.timestamp) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Window -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl h-96 flex flex-col">
            <!-- Chat Header -->
            <div class="p-4 border-b border-slate-700/50">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <span class="text-sm font-bold text-white">{{ selectedChat?.agent.charAt(0) || 'A' }}</span>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white">{{ selectedChat?.agent || 'AI Assistant' }}</h3>
                    <p class="text-sm text-slate-400">{{ selectedChat?.status || 'Ready to help' }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  <span class="text-xs text-emerald-400">Online</span>
                </div>
              </div>
            </div>

            <!-- Chat Messages -->
            <div class="flex-1 p-4 overflow-y-auto space-y-4">
              <div v-if="!selectedChat" class="flex items-center justify-center h-full">
                <div class="text-center">
                  <div class="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                  </div>
                  <h3 class="text-lg font-medium text-white mb-2">Select a conversation</h3>
                  <p class="text-slate-400">Choose from your recent conversations or start a new one</p>
                </div>
              </div>

              <div v-else v-for="message in selectedChat.messages" :key="message.id" class="flex" :class="message.sender === 'user' ? 'justify-end' : 'justify-start'">
                <div class="max-w-xs lg:max-w-md" :class="message.sender === 'user' ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-white'">
                  <div class="p-3 rounded-lg">
                    <p class="text-sm">{{ message.text }}</p>
                    <p class="text-xs opacity-75 mt-1">{{ formatTime(message.timestamp) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Chat Input -->
            <div class="p-4 border-t border-slate-700/50">
              <div class="flex items-center space-x-3">
                <input 
                  v-model="newMessage" 
                  @keyup.enter="sendMessage"
                  type="text" 
                  placeholder="Type your message..."
                  class="flex-1 bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <button @click="sendMessage" class="btn-primary">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Agent Management Section -->
      <div class="mt-8">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-semibold text-white">Your AI Agents</h2>
          <button @click="showCreateAgentModal = true" class="btn-primary">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            Create New Agent
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="agent in userAgents" 
            :key="agent.id"
            class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-800/70 transition-colors"
          >
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center space-x-3">
                <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <span class="text-sm font-bold text-white">{{ agent.name.charAt(0) }}</span>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-white">{{ agent.name }}</h3>
                  <p class="text-sm text-slate-400">{{ agent.type }}</p>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <span :class="[
                  'px-2 py-1 rounded-full text-xs font-medium',
                  agent.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                  agent.status === 'training' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-slate-500/20 text-slate-400'
                ]">
                  {{ agent.status }}
                </span>
              </div>
            </div>
            
            <p class="text-slate-300 text-sm mb-4">{{ agent.description }}</p>
            
            <div class="flex items-center justify-between">
              <div class="text-sm text-slate-400">
                {{ agent.tasksCompleted }} tasks completed
              </div>
              <div class="flex items-center space-x-2">
                <button @click="deployAgent(agent.id)" class="btn-secondary">
                  Deploy
                </button>
                <button @click="editAgent(agent.id)" class="btn-primary">
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Training History -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Training History</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Agent</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Task</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Duration</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="training in trainingHistory" :key="training.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mr-3">
                        <span class="text-xs font-bold text-white">{{ training.agent.charAt(0) }}</span>
                      </div>
                      <span class="text-sm font-medium text-white">{{ training.agent }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ training.task }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      training.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                      training.status === 'in-progress' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    ]">
                      {{ training.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ training.duration }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(training.date) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Create Agent Modal -->
      <div v-if="showCreateAgentModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
          <h3 class="text-xl font-semibold text-white mb-4">Create AI Agent</h3>
          
          <form @submit.prevent="createAgent" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Agent Name</label>
              <input v-model="newAgent.name" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Type</label>
              <select v-model="newAgent.type" class="form-select w-full" required>
                <option value="">Select Type</option>
                <option value="crew-manager">Crew Manager</option>
                <option value="financial-analyst">Financial Analyst</option>
                <option value="compliance-monitor">Compliance Monitor</option>
                <option value="safety-advisor">Safety Advisor</option>
                <option value="project-coordinator">Project Coordinator</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea v-model="newAgent.description" class="form-input w-full" rows="3" required></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showCreateAgentModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Create Agent
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Reactive data
const showCreateAgentModal = ref(false)
const selectedChat = ref(null)
const newMessage = ref('')

const aiStats = ref({
  activeAgents: 0,
  tasksAutomated: 0,
  timeSaved: 0,
  successRate: 0
})

const trainingProgress = ref({
  crewManagement: 75,
  financialAnalysis: 60,
  complianceMonitoring: 85
})

const chatHistory = ref([])
const userAgents = ref([])
const trainingHistory = ref([])

const newAgent = ref({
  name: '',
  type: '',
  description: ''
})

// Methods
const fetchAIStats = async () => {
  try {
    const response = await fetch('/api/company/ai-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      aiStats.value = data
    }
  } catch (error) {
    console.error('Error fetching AI stats:', error)
  }
}

const fetchChatHistory = async () => {
  try {
    const response = await fetch('/api/company/ai-chat-history', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      chatHistory.value = data.chats || []
    }
  } catch (error) {
    console.error('Error fetching chat history:', error)
  }
}

const fetchUserAgents = async () => {
  try {
    const response = await fetch('/api/company/ai-agents', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      userAgents.value = data.agents || []
    }
  } catch (error) {
    console.error('Error fetching user agents:', error)
  }
}

const fetchTrainingHistory = async () => {
  try {
    const response = await fetch('/api/company/ai-training-history', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      trainingHistory.value = data.history || []
    }
  } catch (error) {
    console.error('Error fetching training history:', error)
  }
}

const selectChat = (chat: any) => {
  selectedChat.value = chat
}

const sendMessage = async () => {
  if (!newMessage.value.trim() || !selectedChat.value) return
  
  try {
    const response = await fetch('/api/company/ai-send-message', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        chatId: selectedChat.value.id,
        message: newMessage.value
      })
    })
    
    if (response.ok) {
      newMessage.value = ''
      await fetchChatHistory()
    }
  } catch (error) {
    console.error('Error sending message:', error)
  }
}

const createAgent = async () => {
  try {
    const response = await fetch('/api/company/ai-create-agent', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newAgent.value)
    })
    
    if (response.ok) {
      showCreateAgentModal.value = false
      newAgent.value = { name: '', type: '', description: '' }
      await fetchUserAgents()
    }
  } catch (error) {
    console.error('Error creating agent:', error)
  }
}

const deployAgent = async (agentId: number) => {
  try {
    const response = await fetch('/api/company/ai-deploy-agent', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ agentId })
    })
    
    if (response.ok) {
      await fetchUserAgents()
    }
  } catch (error) {
    console.error('Error deploying agent:', error)
  }
}

const editAgent = (agentId: number) => {
  // Navigate to agent edit page
}

const formatTime = (timestamp: string) => {
  return new Date(timestamp).toLocaleTimeString()
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchAIStats(),
    fetchChatHistory(),
    fetchUserAgents(),
    fetchTrainingHistory()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
